import Typography from 'new/elements/Typography';
import classnames from 'classnames';
import cx from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { LogCopyIconButton } from '../../data_flows/cards/FlowRunLogs/components/LogCopyIconButton';
import DOMUtils from '../../../lib/DOMUtils';
import DateUtils from '../../../lib/DateUtils';
import { Log } from '../../data_flows/cards/FlowRunLogs/types';
import ASCIILoader from '../../elements/ASCIILoader';

import { addUniqueIDsToArrayItems } from 'new/utils';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';
import useMetricsAdapter from 'new/state/metrics/adapter/useMetricsAdapter';
import { useMetricsRepository } from 'new/state/metrics/repository/useMetricsRepository';
import { TIMING, TIME_RANGES } from '../../data_flows/cards/FlowRunLogs/constants';

import styles from './FlowLogsView.module.scss';

interface FlowLogsViewProps {
  scanningMode?: boolean;
  dataSource: any;
  initialMessage?: string;
  onDataSetFound?: () => void;
  onDataSetError?: () => void;
  runId?: number;
  theme?: 'light' | 'dark';
  disablePolling?: boolean;
  fillHeight?: boolean;
}

const errorMessages = [
  'Source folder did not contain any files that qualified for ingestion',
  'Failed to detect a Nexset',
];

const severityElement = (severity, scanningMode) => {
  const severityClassPrefix = scanningMode
    ? styles[`logSeverity-scanning-${severity}`]
    : styles[`logSeverity-${severity}`];

  return (
    <span
      className={classnames(severityClassPrefix, {
        [styles.noSeverity]: !severity,
      })}
    >
      {severity}
    </span>
  );
};

const resourceTypeElement = (log: Log) =>
  log.resource_type ? (
    <Typography className={styles.logLineResourceTypeId}>
      {log.resource_type ? ` [${log.resource_type} ${log.resource_id}]` : ''}
    </Typography>
  ) : null;

const getTimestampString = (log: Log) =>
  DateUtils.getDateString(log.timestamp, 'YYYY-MM-DD HH:mm:ss z');

const cappedContainerStyle = { maxHeight: '300px' };
const fillContainerStyle = { flex: 1, minHeight: 0 };

export const FlowLogsView = ({
  scanningMode = false,
  runId = 0,
  theme = 'dark',
  dataSource,
  initialMessage = '',
  onDataSetFound,
  onDataSetError,
  disablePolling = false,
  fillHeight = false,
}: FlowLogsViewProps) => {
  const isFlowInsightsV2Enabled = useFeatureFlag('UI_ENABLE_FLOW_INSIGHTS_V2');

  const pollFlowRunLogsTimerRef = useRef(0);
  const metricsTimerRef = useRef(0);
  const countErrors = useRef(0);
  const cannotFindNexset = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { getFlowRunLogs, getFlowRunLogsWithFacets } = useMetricsRepository();
  const { requestFlowRunLogsWithFacets, requestFlowRunLogs, requestFlowMetrics } =
    useMetricsAdapter();

  const logsData = (() => {
    if (isFlowInsightsV2Enabled) {
      const logsWithFacetsData = getFlowRunLogsWithFacets();
      const data = logsWithFacetsData?.logs?.[dataSource?.origin_node_id];
      return { data: data ?? [] };
    }

    return getFlowRunLogs(dataSource?.origin_node_id, runId);
  })();

  const { data } = logsData || {};

  const originNodeId = dataSource?.origin_node_id;

  const pollFlowRunLogs = useCallback(() => {
    if (cannotFindNexset.current) return;
    if (!originNodeId) return;

    if (isFlowInsightsV2Enabled) {
      requestFlowRunLogsWithFacets(originNodeId);
      return;
    }

    const from =
      (DateUtils.getCurrentUtcTime(false).valueOf() as number) - TIME_RANGES.FIFTEEN_MINUTES;
    requestFlowRunLogs(originNodeId, { runId, from });
  }, [
    originNodeId,
    runId,
    isFlowInsightsV2Enabled,
    requestFlowRunLogsWithFacets,
    requestFlowRunLogs,
  ]);

  useEffect(() => {
    if (disablePolling) return;

    pollFlowRunLogsTimerRef.current = window.setInterval(
      pollFlowRunLogs,
      TIMING.SCANNING_POLL_INTERVAL,
    );
    pollFlowRunLogs();

    return () => {
      window.clearInterval(pollFlowRunLogsTimerRef.current);
    };
  }, [disablePolling, pollFlowRunLogs]);

  const requestMetricsMemoized = useCallback(() => {
    if (cannotFindNexset.current) return;
    if (!dataSource?.origin_node_id) return;
    requestFlowMetrics(dataSource?.origin_node_id);
  }, [dataSource?.origin_node_id, requestFlowMetrics]);

  useEffect(() => {
    if (disablePolling) return;

    requestMetricsMemoized();

    metricsTimerRef.current = window.setInterval(
      requestMetricsMemoized,
      TIMING.SCANNING_POLL_INTERVAL,
    );

    return () => {
      window.clearInterval(metricsTimerRef.current);
    };
  }, [disablePolling, requestMetricsMemoized]);

  useEffect(() => {
    if (data && data.length) {
      const lastLineIsError = data[data.length - 1]?.log?.includes('error trying to fetch logs');
      const threeErrorsInARow = lastLineIsError && countErrors.current >= 2;
      const dataLength = data.length;
      let someLogFoundNexset = false;
      let someLogCannotFoundNexet = false;
      for (let i = dataLength - 1; i >= 0; i--) {
        const log = data[i];
        someLogFoundNexset = log?.log?.includes('successfully');
        someLogCannotFoundNexet = log?.log?.includes('Failed to detect a Nexset');
        if (someLogFoundNexset || someLogCannotFoundNexet) {
          break;
        }
      }
      if (someLogFoundNexset || threeErrorsInARow) {
        countErrors.current = 0;
        if (onDataSetFound) {
          onDataSetFound();
        }
      }
      if (someLogCannotFoundNexet) {
        cannotFindNexset.current = true;
        countErrors.current = 0;
        if (onDataSetError) {
          onDataSetError();
        }
      }
      if (lastLineIsError) {
        countErrors.current += 1;
      }
    }

    DOMUtils.scrollToBottom(containerRef.current);
  }, [data]);

  const hideLoader = useMemo(() => {
    if (!data) return false;
    let someLogIsErrorMessage = false;
    const dataLength = data.length;
    for (let i = dataLength - 1; i >= 0; i--) {
      const log = data[i];
      someLogIsErrorMessage = errorMessages.includes(log?.log);
      if (someLogIsErrorMessage) {
        return someLogIsErrorMessage;
      }
    }
  }, [data]);

  const logs = useMemo(() => {
    const mock = {
      timestamp: new Date(dataSource.created_at).getTime(),
      log: initialMessage,
    };

    if (!data) return [mock];

    return addUniqueIDsToArrayItems([mock, ...data]);
  }, [data]) as Log[];

  // @Guard: Check if the dataSource is valid
  if (!dataSource || !dataSource.id) return null;

  const containerClassName = cx(styles.container, {
    [styles.container_light]: theme === 'light',
  });

  const logRowClassName = scanningMode ? styles['logRow-scanning'] : styles.logRow;

  return (
    <div
      ref={containerRef}
      style={fillHeight ? fillContainerStyle : cappedContainerStyle}
      className={containerClassName}
    >
      {logs.map((log: Log & { __id: string }) => (
        <div key={log.__id} className={logRowClassName}>
          <LogCopyIconButton
            log={log}
            className={styles.copyButton}
            copiedClassName={styles.copyButton_copied}
          />

          <Typography className={styles.timestamp}>[{getTimestampString(log)}]</Typography>

          {severityElement(log.severity, scanningMode)}

          {resourceTypeElement(log)}

          <Typography
            className={classnames(styles.message, {
              [styles['message-scanning']]: scanningMode,
            })}
          >
            {log.log ?? ''}
          </Typography>
        </div>
      ))}

      {!hideLoader ? (
        <div className={classnames(styles.message, styles.loader)}>
          <ASCIILoader />
        </div>
      ) : null}
    </div>
  );
};
